import { ArrowFloorButton, FloorButton, SecondaryFloorButton } from '@/components/feature/floor-guide';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';
import { NUMBER_OF_FLOORS_PER_VIEW_VERTICAL } from '@/constants/floor-guide';
import { useAccessibilityTTS, useDisableWheelEvent, useScreenSize } from '@/hooks';
import { useVerticalFloorManagement } from '@/hooks/floor-guide';
import { generateAllFloorsGuideWithTTS, generateFloorName, generateFloorNameWithTTS } from '@/utils/floor-guide';
import clsx from 'clsx';
import type { IDataFloor } from 'dabeeomaps/dist/src/model/map/IDataFloor';
import type { IDataFloorName } from 'dabeeomaps/dist/src/model/map/IDataFloorName';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import styles from './styles.module.scss';

type Props = {
    floors?: IDataFloor[];
    selectedFloorName?: string;
    isShowAllFloorGuide: boolean;
    onSelectFloor?: (floorName: string) => void;
    setIsShowAllFloorGuide: (isShow: boolean) => void;
    onShowMyLocation: () => void;
    currentMapBuildingId?: string;
};

export const VerticalFloorList = ({ floors, selectedFloorName, onSelectFloor, isShowAllFloorGuide, setIsShowAllFloorGuide, onShowMyLocation }: Props) => {
    const { t } = useTranslation();
    const { scaleHeightPixel } = useScreenSize();
    const { speak } = useAccessibilityTTS();
    const buttonText = t('common.button.tts.button');
    const inactiveButtonText = t('common.button.tts.inactive_button');

    // Sync sort in render so hook index and Swiper slides stay aligned when floors change
    const sortedFloors = useMemo(() => {
        if (!Array.isArray(floors) || !floors.length) return [];
        return [...floors].sort((a, b) => a.order - b.order);
    }, [floors]);

    const [lastInteractionWasMouse, setLastInteractionWasMouse] = useState(false);

    const handleMyLocationMouseDown = () => {
        setLastInteractionWasMouse(true);
    };

    const handleMyLocationFocus = () => {
        if (!lastInteractionWasMouse) {
            speak(`${t('floor_guide.button.current_location')} ${t('common.data_role.button')}`);
        }
        setLastInteractionWasMouse(false);
    };

    const isShowFloors = useMemo(() => sortedFloors.length > 0, [sortedFloors]);

    // State to track current visible index of Swiper
    const [currentActiveIndex, setCurrentActiveIndex] = useState(0);

    const swiperContainerRef = useRef<HTMLDivElement>(null);

    // Use custom hook to disable wheel events on Swiper container
    useDisableWheelEvent(swiperContainerRef);

    const renderFloorName = useCallback((name: IDataFloorName[]) => {
        if (!Array.isArray(name) || !name?.length) return '';
        return name[0]?.text ?? '-';
    }, []);

    const { handleScrollUp, handleScrollDown, shouldScroll, swiperRef, handleDisableNext, handleDisablePrev, isDisabledPrev, isDisabledNext } =
        useVerticalFloorManagement(sortedFloors, NUMBER_OF_FLOORS_PER_VIEW_VERTICAL, selectedFloorName);

    const handleAllFloorsGuide = () => {
        setIsShowAllFloorGuide(!isShowAllFloorGuide);
    };

    const isSelectedItem = useCallback(
        (floor?: IDataFloor) => {
            if (!floor || !Array.isArray(floor.name) || !floor.name?.length) {
                return false;
            }
            return floor.name[0]?.text === selectedFloorName;
        },
        [selectedFloorName]
    );

    const handleSelectFloor = useCallback(
        (floor?: IDataFloor) => {
            if (!floor || !Array.isArray(floor.name) || !floor.name?.length) {
                return;
            }
            const floorName = floor.name[0]?.text ?? '';

            speak?.(t('floor_guide.text_to_speech.move_to_floor', { floorName: generateFloorName(floorName) }));
            onSelectFloor?.(floorName);
        },
        [onSelectFloor, speak, t]
    );

    const handleFocusFloorBtn = useCallback(
        (floor: IDataFloor, selected: boolean) => {
            if (!floor || !Array.isArray(floor.name) || !floor.name?.length) return;
            speak(generateFloorNameWithTTS(t, t('floor_guide.text_to_speech.floor', { floorName: generateFloorName(floor.name[0]?.text ?? '') }), selected));
        },
        [speak, t]
    );

    // Voice handling for arrow buttons
    const handleScrollUpWithTTS = useCallback(() => {
        if (isDisabledPrev) {
            speak(t('floor_guide.text_to_speech.click.inactive_prev_floor_btn'));
        } else {
            speak(t('floor_guide.text_to_speech.click.active_prev_floor_btn'));
            handleScrollUp();
        }
    }, [handleScrollUp, isDisabledPrev, speak, t]);

    const handleScrollDownWithTTS = useCallback(() => {
        if (isDisabledNext) {
            speak(t('floor_guide.text_to_speech.click.inactive_next_floor_btn'));
        } else {
            speak(t('floor_guide.text_to_speech.click.active_next_floor_btn'));
            handleScrollDown();
        }
    }, [handleScrollDown, isDisabledNext, speak, t]);

    // Calculate which floor items are visible based on activeIndex and slidesPerView
    const visibleFloorIndexes = useMemo(() => {
        if (!sortedFloors.length) return [];

        const visibleIndexes = new Set<number>();
        const startIndex = currentActiveIndex;
        const endIndex = Math.min(startIndex + NUMBER_OF_FLOORS_PER_VIEW_VERTICAL, sortedFloors.length);

        for (let i = startIndex; i < endIndex; i++) {
            visibleIndexes.add(i);
        }

        return Array.from(visibleIndexes);
    }, [currentActiveIndex, sortedFloors.length]);

    // Function to determine if a floor item should have tabIndex
    const getFloorTabIndex = useCallback(
        (floorIndex: number) => {
            return visibleFloorIndexes.includes(floorIndex) ? 9 : -1;
        },
        [visibleFloorIndexes]
    );

    return (
        <section
            className={clsx(styles['floor-list'], {
                [styles['floor-list--scrollable']]: shouldScroll,
            })}
        >
            <section className="flex gap-[calc(10*var(--vh-unit))]" data-region-nav data-region-order={7}>
                <SecondaryFloorButton
                    tabIndex={8}
                    data-interactive
                    onClick={() => handleAllFloorsGuide()}
                    isSelected={isShowAllFloorGuide}
                    className="w-[calc(104*var(--vh-unit))]"
                    aria-label={generateAllFloorsGuideWithTTS(t, t('floor_guide.button.all_floors_guide'), isShowAllFloorGuide)}
                >
                    {t('floor_guide.button.all_floors_guide')}
                </SecondaryFloorButton>
                <SecondaryFloorButton
                    role="button"
                    data-tts-scope="my-location-button"
                    tabIndex={8}
                    className="w-[calc(104*var(--vh-unit))]"
                    onMouseDown={handleMyLocationMouseDown}  // Click chuột → set flag
                    onFocus={handleMyLocationFocus}          // Focus event
                    onClick={() => onShowMyLocation()}
                >
                    {t('floor_guide.button.current_location')}
                </SecondaryFloorButton>
            </section>

            {
                isShowFloors && (
                    <section className={styles['floor-list__items']}>
                        {shouldScroll && (
                            <ArrowFloorButton
                                data-region-nav
                                data-type="button"
                                data-region-order={9}
                                data-tts-scope="arrow-floor-button"
                                tabIndex={9}
                                data-interactive
                                className="swiper-prev absolute left-0 z-10"
                                aria-disabled={isDisabledPrev}
                                onClick={() => handleScrollUpWithTTS()}
                                onFocus={() => {
                                    if (isDisabledPrev) {
                                        speak(`${t('floor_guide.text_to_speech.focus.inactive_prev_floor_btn')} ${inactiveButtonText}`);
                                    } else {
                                        speak(`${t('floor_guide.text_to_speech.focus.active_prev_floor_btn')} ${buttonText}`);
                                    }
                                }}
                            >
                                <ChevronLeftIcon />
                            </ArrowFloorButton>
                        )}

                        <Swiper
                            mousewheel={false}
                            allowTouchMove={true}
                            simulateTouch={false}
                            preventInteractionOnTransition
                            ref={swiperRef}
                            modules={[Navigation]}
                            slidesPerView={NUMBER_OF_FLOORS_PER_VIEW_VERTICAL}
                            slidesPerGroup={NUMBER_OF_FLOORS_PER_VIEW_VERTICAL}
                            spaceBetween={scaleHeightPixel(8)}
                            navigation={{
                                prevEl: '.swiper-prev',
                                nextEl: '.swiper-next',
                            }}
                            loop={false}
                            onSlideChange={(swiper) => {
                                handleDisablePrev(swiper.isBeginning);
                                handleDisableNext(swiper.isEnd);
                                setCurrentActiveIndex(swiper.activeIndex);
                            }}
                            onInit={(swiper) => {
                                handleDisablePrev(swiper.isBeginning);
                                handleDisableNext(swiper.isEnd);
                                setCurrentActiveIndex(swiper.activeIndex);
                            }}
                            className="flex-1 overflow-hidden"
                            data-region-nav
                            data-region-order={9}
                        >
                            {sortedFloors?.map((floor, index) => (
                                <SwiperSlide key={floor.id} className="flex !w-auto items-center justify-center">
                                    <FloorButton
                                        data-tts-scope="floor-button"
                                        tabIndex={getFloorTabIndex(index)}
                                        data-interactive
                                        isSelected={isSelectedItem(floor)}
                                        onClick={() => handleSelectFloor(floor)}
                                        onFocus={() => handleFocusFloorBtn(floor, isSelectedItem(floor))}
                                    >
                                        {renderFloorName(floor.name)}
                                    </FloorButton>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        {shouldScroll && (
                            <ArrowFloorButton
                                data-tts-scope="arrow-floor-button"
                                className="swiper-next absolute right-0 z-10 w-[calc(56*var(--vh-unit))]"
                                aria-disabled={isDisabledNext}
                                data-type="button"
                                onClick={() => handleScrollDownWithTTS()}
                                tabIndex={11}
                                data-interactive
                                data-region-nav
                                data-region-order={11}
                                onFocus={() => {
                                    if (isDisabledNext) {
                                        speak(`${t('floor_guide.text_to_speech.focus.inactive_next_floor_btn')} ${inactiveButtonText}`);
                                    } else {
                                        speak(`${t('floor_guide.text_to_speech.focus.active_next_floor_btn')} ${buttonText}`);
                                    }
                                }}
                            >
                                <ChevronRightIcon />
                            </ArrowFloorButton>
                        )}
                    </section>
                )
            }
        </section >
    );
};
